import type { PlanApprovalState } from '@/domain/plan/planApprovalState';
import {
    initialVisibilityToggles,
    type VisibilityToggles,
} from '@/views/3d-planner/measurements/visibilityToggles';
import type { Vector3 } from 'three';
import { zero3 } from '@/domain/geometry/vector3';
import type { ComponentCatalog } from '@/domain/catalog/catalog';
import type { ApiFittedComponents } from '@/api/fittedComponents/getFittedComponents';
import type { SideId } from '@/domain/anatomy/side';
import type { Url } from '@/domain/types';
import { LpsCoordinateSystem, type LpsVectors } from '@/domain/anatomy/LPS';
import type { ManualTemplateState } from '@/views/3d-planner/template/manualTemplateState';
import type { AxisMeasurement } from '@/domain/anatomy/measurements';
import type { NumberArray3 } from '@/domain/geometry/apiVector';
import type { CatstacksData } from '@/api/catstacksData';
import {
    initialPlannerCatstacksState,
    type PlannerCatstacksState,
} from '@/views/3d-planner/store/catstacksState';
import type { PlannerOperationId } from '@/views/3d-planner/operations/executeOperation';
import type { Template } from '@/domain/template/template';
import type { ApiCompleteSpecification } from '@/views/3d-planner/api/specification';
import type { ImageModality } from '@/api/study/study';
import type { ApiBoneId } from '@/api/study/studyModels';

/**
 * Different modes for rendering the objects in the 3D scene
 *
 * - 'normal': Standard shaded and lit appearance
 * - 'xray': Surfaces are transparent dependent on the angle they are viewed, becoming more opaque
 *   as they become tangential to the view direction.
 */
export type RenderingMode = 'normal' | 'xray';

export const plannerModes = ['default', 'cup', 'stem', 'disabled'] as const;

export type PlannerMode = (typeof plannerModes)[number];

export type CaseInfo = {
    caseId: number;
    caseUrl: Url;
    caseVersion: string;
    operationalSide: SideId;
    autoTemplate: Template;
    manualTemplateUrl: Url;
    manualTemplateCanonicalUrl: Url;
    /**
     * The template history the current manual plan was made from, or null when there is none.
     *
     * Formerly `manualTemplateLastApprovedUrl`. A plan existing no longer means a surgeon accepted
     * it, so the old name asserted something this field cannot answer - `manualPlanApprovalState`
     * is what answers it.
     */
    manualPlanTemplateUrl: Url | null;

    /** Whether that plan was approved by a surgeon, or only proposed for one. */
    manualPlanApprovalState: PlanApprovalState | null;

    /**
     * The act this user performs on this case, as the server named it: the state a plan they
     * created now would carry, or null when they may create neither.
     */
    callerPlanApprovalState: PlanApprovalState | null;
    specification: ApiCompleteSpecification;
    sourceModality: ImageModality;
};

/** Optimisation targets */
export type Adjustments = {
    /** The change in leg-length */
    legLength: number;

    /** The change in offset */
    offset: number;
};

/** Native measurements to be displayed in the overlay view */
export type NativeMeasurements = {
    hipLengthDifference: number | null;
    femoralAnteversion: number | null;
    acetabularAnteversion: number | null;
    acetabularInclination: number | null;
};

export type FemurDiceScores = {
    operativeFemurDiceScore: number | null;
    contralateralFemurDiceScore: number | null;
};

export type FemoralFeatures = {
    shaftAxis: AxisMeasurement;
    proximalShaftAxis: AxisMeasurement;
    neckAxis: AxisMeasurement;
    anteversionNeck: NumberArray3;
    anteversionCondylar: NumberArray3;
    stemTranslationBasis: LpsVectors;
    shaftBasis: LpsVectors;
};

/** Resolved geometry-source url per anatomical bone, projected onto scene nodes by the scene VM. */
export type BoneSources = Partial<Record<ApiBoneId, Url | null>>;

/**
 * The domain state of the planner: raw case/template data, API-loaded measurements and
 * the operation lifecycle. It deliberately holds **no** scene or presentation state —
 * that lives on `PlannerViewState` (owned by the scene ViewModel). See
 * docs/threejs-practices.md → "Where scene state lives".
 */
export type PlannerState = {
    _sync: boolean;
    currentOperation: PlannerOperationId | null;

    // Loaded state
    case: CaseInfo | null;
    sceneCentre: Vector3;
    autoTemplate: Template | null;
    template: ManualTemplateState | null;

    /**
     * The target adjustments that are currently being displayed in the planner.
     * These are used for every calculation in the planner (e.g.: warning adjustments)
     * These are defaulted in the following order:
     * - the template targets
     * - the specification targets
     * - the preferences targets
     *
     * Note: that the targets in the template can be null (as it is usually the case
     * when the manual template is created for the first time).
     */
    alignmentCS: LpsCoordinateSystem | null;
    /** APP coordinate system (APP axes in CT-LPS space), always computed from measurements. */
    appCS: LpsCoordinateSystem | null;
    targetAdjustments: Adjustments | null;
    catstacksData: CatstacksData | null;
    nativeMeasurements: NativeMeasurements;
    diceScores: FemurDiceScores | null;
    femoralFeatures: FemoralFeatures | null;
    ltResectionPoint: NumberArray3 | null;
    catalog: ComponentCatalog | null;
    fittedComponents: ApiFittedComponents | null;
    boneSources: BoneSources | null;
};

/**
 * The presentation state of the planner — scene mode, rendering mode, layer visibility and
 * catstack display. Owned by the scene ViewModel (`usePlannerSceneViewModel`), never the Model.
 */
export type PlannerViewState = {
    plannerMode: PlannerMode;
    renderingMode: RenderingMode;
    visibility: VisibilityToggles;
    catstacks: PlannerCatstacksState;
};

/**
 * Create the initial planner domain-state
 */
export function plannerState(): PlannerState {
    return {
        _sync: false,
        currentOperation: null,

        case: null,
        autoTemplate: null,
        template: null,
        targetAdjustments: null,
        catstacksData: null,
        nativeMeasurements: {
            hipLengthDifference: null,
            femoralAnteversion: null,
            acetabularAnteversion: null,
            acetabularInclination: null,
        },
        alignmentCS: null,
        appCS: null,
        diceScores: null,
        femoralFeatures: null,
        ltResectionPoint: null,
        sceneCentre: zero3(),
        catalog: null,
        fittedComponents: null,
        boneSources: null,
    };
}

/**
 * Create the initial planner view-state
 */
export function plannerViewState(): PlannerViewState {
    return {
        plannerMode: 'disabled',
        renderingMode: 'normal',
        visibility: initialVisibilityToggles('default'),
        catstacks: initialPlannerCatstacksState(),
    };
}

export function resetPlannerState(state: PlannerState) {
    // Assign all properties from the initial state except those in NO_RESET_PROPERTIES
    const NO_RESET_PROPERTIES = ['catalog', 'currentOperation'];
    const resetProperties = Object.fromEntries(
        Object.entries(plannerState()).filter(([name]) => !NO_RESET_PROPERTIES.includes(name)),
    );
    Object.assign(state, resetProperties);
}
